/**
 * The GA4 affiliate_click event: one per outbound affiliate or store click, with the vendor, the compound, the
 * placement, the link's domain and the reader's first-touch source and medium (lib/firstTouch.ts). GA4's own
 * outbound-click event does not fire on the site's same-domain /go/<vendor> hop, and it knows nothing about vendors
 * or placements, so without this no tool joined a click to the channel that brought the reader.
 *
 * lib/analytics.ts sends it, from the click helpers (trackAffiliateClick, trackBuyClick) and from a document click
 * listener for links no helper covers. This file only works out the parameters, so the rules are testable.
 */
import { compoundSlugFromPath, pageCategory } from "@/lib/pageCategory";
import type { FirstTouch } from "@/lib/firstTouch";

/** Lower case, letters, digits and . _ : - only, at most 100 characters: "BHG Labs" is "bhg-labs". */
export function clickLabel(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9._:-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

const SITE_HOST = /^([a-z0-9-]+\.)*bodyhackguide\.co$/;
const STORE_HOST = /^([a-z0-9-]+\.)*bhglabs\.co$/;
const GO_FUNCTION = /^\/functions\/v1\/go\/([^/]+)\/?$/;

function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "");
}

/** The vendor a plain outbound link points at: "www.researchchemhq.co" is "researchchemhq". */
function vendorFromHost(host: string): string {
  const labels = bareHost(host).split(".");
  if (labels.length < 2) return clickLabel(labels[0]);
  // example.co.uk: the name is before the two-part ending.
  const name = labels.length > 2 && /^(co|com|org|net)$/.test(labels[labels.length - 2]) ? labels[labels.length - 3] : labels[labels.length - 2];
  return clickLabel(name);
}

export interface AffiliateLink {
  kind: "go" | "store" | "sponsored";
  vendor: string;
  placement: string;
  link_domain: string;
}

/**
 * Whether a clicked link is an affiliate or store link, and what it says about itself:
 *   - the site's own /go/<slug> redirect (or the go function behind it): vendor = slug, placement = cta;
 *   - a BHG Labs store link: vendor "bhg-labs", placement = utm_content (storeUrl's placement label);
 *   - any other outbound link marked rel="sponsored" (every affiliate link is): vendor from the host name.
 * Anything else is null.
 */
export function classifyAffiliateLink(href: string, rel: string | null | undefined, pageOrigin: string): AffiliateLink | null {
  let url: URL;
  try {
    url = new URL(href, pageOrigin);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase();
  const param = (name: string) => url.searchParams.get(name) ?? "";

  const own = url.origin === pageOrigin || SITE_HOST.test(host);
  const goSlug = own ? url.pathname.match(/^\/go\/([^/]+)\/?$/)?.[1] : /\.supabase\.co$/.test(host) ? url.pathname.match(GO_FUNCTION)?.[1] : undefined;
  if (goSlug) {
    let slug = goSlug;
    try {
      slug = decodeURIComponent(goSlug);
    } catch {
      /* keep it as written */
    }
    // Links inside the site name their placement in cta (lib/goLink.ts); older ones in the database still use utm_*.
    return { kind: "go", vendor: clickLabel(slug), placement: clickLabel(param("cta") || param("utm_content") || param("utm_source")), link_domain: bareHost(host) };
  }
  if (own) return null;
  if (STORE_HOST.test(host)) {
    return { kind: "store", vendor: "bhg-labs", placement: clickLabel(param("utm_content")), link_domain: bareHost(host) };
  }
  if (/(^|\s)sponsored(\s|$)/i.test(rel ?? "")) {
    return { kind: "sponsored", vendor: vendorFromHost(host), placement: "", link_domain: bareHost(host) };
  }
  return null;
}

export interface AffiliateClickInput {
  vendor?: unknown;
  compound?: unknown;
  placement?: unknown;
  /** The clicked link's URL, when there is one; link_domain comes from it. */
  href?: string | null;
  value?: unknown;
}

export interface AffiliateClickContext {
  pathname: string;
  origin: string;
  firstTouch: FirstTouch | null;
  /** The clicked link's rel attribute, when the click came from a link. */
  rel?: string | null;
}

/**
 * The GA4 parameters. A value the caller passes wins over one read off the link; the compound falls back to the
 * page's own compound and the placement to the page type (lib/pageCategory.ts). Empty values are left out, so GA4
 * reports them as "(not set)" instead of an empty string.
 */
export function affiliateClickParams(input: AffiliateClickInput, ctx: AffiliateClickContext): Record<string, string | number> {
  const link = input.href ? classifyAffiliateLink(input.href, ctx.rel ?? "sponsored", ctx.origin) : null;
  let linkDomain = link?.link_domain ?? "";
  if (!linkDomain && input.href) {
    try {
      linkDomain = bareHost(new URL(input.href, ctx.origin).hostname);
    } catch {
      /* no domain */
    }
  }
  const params: Record<string, string | number> = {
    vendor: clickLabel(input.vendor) || link?.vendor || "",
    compound: clickLabel(input.compound) || compoundSlugFromPath(ctx.pathname),
    placement: clickLabel(input.placement) || link?.placement || pageCategory(ctx.pathname),
    link_domain: linkDomain,
    first_touch_source: ctx.firstTouch?.source ?? "",
    first_touch_medium: ctx.firstTouch?.medium ?? "",
  };
  if (typeof input.value === "number" && Number.isFinite(input.value)) {
    params.value = input.value;
    params.currency = "USD";
  }
  for (const key of Object.keys(params)) if (params[key] === "") delete params[key];
  return params;
}
